/**
 * Newsletter (customer) signup — post-submit enhancement.
 *
 * IMPORTANT: the form submits NATIVELY (a normal top-level page navigation).
 * We deliberately do NOT take it over with fetch() or a hidden iframe, because
 * Shopify only records the customer for genuine page-navigation POSTs. Any
 * background submission (fetch, or a form targeted at a hidden iframe) is
 * silently dropped by Shopify's bot protection — which is SEPARATE from the
 * storefront spam/CAPTCHA setting — so it would show a success state while
 * creating no customer at all. A real reload is the only reliable path.
 *
 * What this script does instead is make the returned ?customer_posted=true
 * page feel like a seamless transition: reveal the success state, scroll the
 * newsletter section into view, and tidy the URL. The success state is ALSO
 * rendered server-side (via `form.posted_successfully?` in the section), so it
 * is correct even with JavaScript disabled — this is pure enhancement.
 */
(function () {
  if (window.__toppikNewsletterAjax) return;
  window.__toppikNewsletterAjax = true;

  const SUCCESS_REDIRECT = /[?&]customer_posted=true/;

  const show = (node) => {
    if (node) node.hidden = false;
  };
  const hide = (node) => {
    if (node) node.hidden = true;
  };

  function isNewsletterForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    const type = form.querySelector('input[name="form_type"]');
    if (type && type.value === 'customer') return true;
    // Fallbacks so we never miss the form: our known newsletter forms, or any
    // form posting a contact[email] field (Shopify customer email capture).
    if (form.classList.contains('email-signup__form') || form.classList.contains('fibers-inner__form')) return true;
    if (form.querySelector('input[name="contact[email]"]')) return true;
    return false;
  }

  // On the reloaded ?customer_posted=true page, reveal the success state on our
  // newsletter form(s), scroll the section into view, then clean the URL so a
  // refresh doesn't re-trigger it.
  function handleCustomerPosted() {
    if (!SUCCESS_REDIRECT.test(location.search)) return;

    const forms = Array.prototype.slice.call(document.querySelectorAll('form')).filter(isNewsletterForm);
    forms.forEach((form) => {
      const success = form.querySelector('[data-newsletter-success]');
      if (!success) return;
      hide(form.querySelector('[data-newsletter-fields]'));
      show(success);
    });

    const visibleSuccess = document.querySelector('[data-newsletter-success]:not([hidden])');
    const target = visibleSuccess && (visibleSuccess.closest('.fibers-inner') || visibleSuccess);
    if (target && target.scrollIntoView) {
      // Land on the section showing "You're in!" rather than at the top.
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    try {
      if (window.history && history.replaceState) {
        // Drop ?customer_posted=true (and the #contact_form hash) from the URL.
        history.replaceState({}, document.title, location.pathname);
      }
    } catch (e) {
      /* no-op */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleCustomerPosted);
  } else {
    handleCustomerPosted();
  }

  /* ---------------------------------------------------------------------------
   * Klaviyo direct submit (no reload) — keeps the section's own input + success
   * design. When a newsletter form carries data-klaviyo-list +
   * data-klaviyo-company, submit straight to Klaviyo's client-side subscription
   * API over fetch and reveal the existing success state in place — no page
   * navigation. Forms without those attributes keep the native Shopify path
   * above. The company id is Klaviyo's PUBLIC key (safe to expose client-side).
   * ------------------------------------------------------------------------- */
  const KLAVIYO_REVISION = '2024-10-15';

  function klaviyoConfig(form) {
    const list = (form.dataset.klaviyoList || '').trim();
    const company = (form.dataset.klaviyoCompany || '').trim();
    return list && company ? { list, company } : null;
  }

  async function submitToKlaviyo(form, cfg) {
    const emailInput = form.querySelector('input[name="contact[email]"], input[type="email"]');
    const email = emailInput && emailInput.value.trim();
    if (!email) return;

    const fields = form.querySelector('[data-newsletter-fields]');
    const success = form.querySelector('[data-newsletter-success]');
    const error = form.querySelector('[data-newsletter-error]');
    const button = form.querySelector('button[type="submit"], button:not([type])');

    hide(error);
    if (button) button.disabled = true;

    try {
      const response = await fetch(
        'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.company),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: { profile: { data: { type: 'profile', attributes: { email: email } } } },
              relationships: { list: { data: { type: 'list', id: cfg.list } } },
            },
          }),
        }
      );
      if (!response.ok) throw new Error('Klaviyo responded ' + response.status);
      hide(fields);
      show(success);
      if (success && typeof success.focus === 'function') success.focus();
    } catch (e) {
      if (error) {
        error.textContent = 'Sorry, something went wrong. Please try again.';
        show(error);
      }
    } finally {
      if (button) button.disabled = false;
    }
  }

  // Intercept on the capture phase so we stop the native submit before it fires.
  // The browser has already enforced required/type=email validation by now.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!isNewsletterForm(form)) return;
      const cfg = klaviyoConfig(form);
      if (!cfg) return; // no Klaviyo configured -> native Shopify submit
      event.preventDefault();
      submitToKlaviyo(form, cfg);
    },
    true
  );
})();
